import { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { Map as MapIcon } from 'lucide-react';
import { db } from '../../firebase/config';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';

const Maps = ({ tournamentId }) => {
  const [maps, setMaps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const mapsRef = tournamentId
          ? collection(db, 'tournaments', tournamentId, 'maps')
          : collection(db, 'maps');
        const snap = await getDocs(mapsRef);
        if (!cancelled) {
          setMaps(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        }
      } catch {
        // anonymous reads may be restricted; render empty
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [tournamentId]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="h-60 animate-pulse rounded-md border border-border/60 bg-muted/10"
          />
        ))}
      </div>
    );
  }

  if (maps.length === 0) {
    return (
      <div className="border border-dashed border-border/60 px-6 py-12 text-center text-sm text-muted-foreground">
        <MapIcon className="mx-auto mb-3 h-6 w-6 text-muted-foreground/50" />
        No maps configured for this tournament yet.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
      {maps.map((map) => (
        <Card
          key={map.id}
          className="flex flex-col overflow-hidden border-border/60 bg-background/40"
        >
          {map.imageUrl ? (
            <div className="relative h-44 w-full overflow-hidden border-b border-border/60">
              <img src={map.imageUrl} alt={map.name} className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent" />
            </div>
          ) : (
            <div className="h-2 w-full bg-gradient-to-r from-primary/30 via-heat/20 to-transparent" />
          )}
          <CardContent className="flex flex-1 flex-col gap-3 pt-5">
            <h3 className="font-display text-lg font-semibold tracking-tight">{map.name}</h3>
            <div className="flex flex-wrap gap-1.5">
              {map.tier && <Badge variant="outline">Tier {map.tier}</Badge>}
              {map.mapper && <Badge variant="outline">by {map.mapper}</Badge>}
              {map.timeLimit && <Badge variant="outline">{map.timeLimit}</Badge>}
              {map.difficulty && <Badge variant="outline">Difficulty {map.difficulty}</Badge>}
            </div>
            {map.description && <p className="text-sm text-muted-foreground">{map.description}</p>}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default Maps;
